'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  Smartphone,
  Star,
  MessageCircle,
  BookOpen,
  Dumbbell,
  ShoppingBag,
  CheckCircle2,
  Bot,
  Heart,
  Shield,
  Users
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

interface Review {
  id: string;
  firstName: string;
  rating: number;
  comment: string;
  createdAt: string;
}

interface AnimatedHomeProps {
  reviews: Review[];
}

export default function AnimatedHome({ reviews }: AnimatedHomeProps) {
  const heroRef = useRef<HTMLDivElement>(null);
  const heroContentRef = useRef<HTMLDivElement>(null);
  const heroImageRef = useRef<HTMLDivElement>(null);
  const downloadRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Wait for next frame to ensure DOM is ready after hydration
    const timeoutId = setTimeout(() => {
      const ctx = gsap.context(() => {
        // Hero animations
        if (heroContentRef.current && heroContentRef.current.children.length > 0) {
          gsap.fromTo(
            heroContentRef.current.children,
            { y: 40, opacity: 0 },
            { y: 0, opacity: 1, duration: 1, stagger: 0.15, ease: 'power2.out' }
          );
        }

        if (heroImageRef.current) {
          gsap.fromTo(
            heroImageRef.current,
            { scale: 0.9, opacity: 0 },
            { scale: 1, opacity: 1, duration: 1.2, ease: 'power2.out', delay: 0.3 }
          );

          // Floating animation for hero image
          gsap.to(heroImageRef.current, {
            y: -12,
            duration: 4,
            ease: 'power1.inOut',
            repeat: -1,
            yoyo: true,
            delay: 1.5,
          });
        }

        // Download section
        if (downloadRef.current) {
          const phones = downloadRef.current.querySelectorAll('.phone-screenshot');
          if (phones.length > 0) {
            gsap.fromTo(
              phones,
              { y: 50, opacity: 0 },
              {
                y: 0,
                opacity: 1,
                duration: 0.8,
                stagger: 0.15,
                ease: 'power2.out',
                scrollTrigger: {
                  trigger: downloadRef.current,
                  start: 'top 85%',
                },
              }
            );
          }
        }

        // Steps section
        if (stepsRef.current) {
          const steps = stepsRef.current.querySelectorAll('.step-card');
          if (steps.length > 0) {
            gsap.fromTo(
              steps,
              { y: 40, opacity: 0 },
              {
                y: 0,
                opacity: 1,
                duration: 0.7,
                stagger: 0.12,
                ease: 'power2.out',
                scrollTrigger: {
                  trigger: stepsRef.current,
                  start: 'top 80%',
                },
              }
            );
          }
        }

        // Features section
        if (featuresRef.current) {
          const features = featuresRef.current.querySelectorAll('.feature-card');
          if (features.length > 0) {
            gsap.fromTo(
              features,
              { y: 30, opacity: 0 },
              {
                y: 0,
                opacity: 1,
                duration: 0.6,
                stagger: 0.08,
                ease: 'power2.out',
                scrollTrigger: {
                  trigger: featuresRef.current,
                  start: 'top 85%',
                },
              }
            );
          }

          // Floating images
          const floatingImages = featuresRef.current.querySelectorAll('.floating-image');
          floatingImages.forEach((img, i) => {
            gsap.to(img, {
              y: -8 - (i * 4),
              duration: 3.5 + (i * 0.5),
              ease: 'power1.inOut',
              repeat: -1,
              yoyo: true,
            });
          });
        }

        // Reviews section
        if (reviewsRef.current) {
          const reviewCards = reviewsRef.current.querySelectorAll('.review-card');
          if (reviewCards.length > 0) {
            gsap.fromTo(
              reviewCards,
              { y: 40, opacity: 0 },
              {
                y: 0,
                opacity: 1,
                duration: 0.7,
                stagger: 0.1,
                ease: 'power2.out',
                scrollTrigger: {
                  trigger: reviewsRef.current,
                  start: 'top 80%',
                },
              }
            );
          }
        }

        // About section
        if (aboutRef.current) {
          const aboutContent = aboutRef.current.querySelector('.about-content');
          const aboutImage = aboutRef.current.querySelector('.about-image');

          if (aboutContent) {
            gsap.fromTo(
              aboutContent,
              { x: -40, opacity: 0 },
              {
                x: 0,
                opacity: 1,
                duration: 0.8,
                ease: 'power2.out',
                scrollTrigger: {
                  trigger: aboutRef.current,
                  start: 'top 75%',
                },
              }
            );
          }

          if (aboutImage) {
            gsap.fromTo(
              aboutImage,
              { x: 40, opacity: 0 },
              {
                x: 0,
                opacity: 1,
                duration: 0.8,
                ease: 'power2.out',
                scrollTrigger: {
                  trigger: aboutRef.current,
                  start: 'top 75%',
                },
              }
            );
          }
        }

        // CTA section
        if (ctaRef.current) {
          gsap.fromTo(
            ctaRef.current,
            { y: 30, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.7,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: ctaRef.current,
                start: 'top 90%',
              },
            }
          );
        }
      });

      return () => ctx.revert();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-4 md:inset-6 lg:inset-8 bg-gradient-to-br from-cta via-cta-dark to-ink rounded-3xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-cta/80 via-transparent to-cta-dark/60" />
        </div>

        <div className="absolute bottom-12 right-12 md:bottom-16 md:right-16 z-10">
          <Image
            src="/images/icon-white.svg"
            alt=""
            width={120}
            height={120}
            className="w-20 h-20 md:w-28 md:h-28 lg:w-32 lg:h-32 opacity-30"
            aria-hidden="true"
          />
        </div>

        <div className="relative w-full py-32 lg:py-40 px-8 md:px-12 lg:px-16">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <div ref={heroContentRef} className="order-2 lg:order-1">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-normal leading-tight mb-6">
                Veilig thuis,<br />
                <span className="font-handwriting text-yellow-300">zorgeloos leven</span>
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
                De ValBewust app helpt u en uw dierbaren om veilig en zelfstandig thuis te blijven wonen. Met praktische tips, oefeningen en persoonlijke begeleiding.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link href="#download" className="btn-primary bg-white text-cta hover:bg-yellow-300 hover:text-cta-dark">
                  <Smartphone className="mr-2" size={20} />
                  Download Gratis
                </Link>
                <Link href="#hoe-werkt-het" className="btn-outline-white">
                  Meer informatie
                </Link>
              </div>
            </div>

            <div ref={heroImageRef} className="order-1 lg:order-2 flex justify-center">
              <div className="relative">
                <div className="w-72 h-72 md:w-96 md:h-96 rounded-full overflow-hidden border-8 border-white/20 shadow-2xl">
                  <Image
                    src="/images/hero-mother-daughter.jpg"
                    alt="Oudere moeder en dochter delen een warm moment"
                    width={384}
                    height={384}
                    sizes="(max-width: 768px) 288px, 384px"
                    className="w-full h-full object-cover"
                    priority
                    fetchPriority="high"
                  />
                </div>
                <div className="absolute -top-4 -left-4 w-20 h-20 bg-yellow-400/20 rounded-full" />
                <div className="absolute -bottom-8 left-8 w-12 h-12 bg-white/10 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="section-padding bg-white">
        <div className="container-site">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-normal text-ink mb-4">
              Download de <span className="font-handwriting text-cta">ValBewust</span> App
            </h2>
            <p className="text-lg text-ink/70">
              Beschikbaar voor iPhone en Android. Gratis te downloaden.
            </p>
          </div>

          <div ref={downloadRef} className="grid lg:grid-cols-3 gap-8 items-center relative">
            <div className="absolute -top-8 left-1/4 w-16 h-16 bg-cta/10 rounded-full hidden lg:block" />
            <div className="absolute -bottom-8 right-1/4 w-20 h-20 bg-yellow-400/20 rounded-full hidden lg:block" />

            <div className="hidden lg:flex justify-end phone-screenshot">
              <div className="relative">
                <div className="w-48 bg-gradient-to-br from-cta/10 to-cta/5 rounded-[2rem] p-3 shadow-lg transform rotate-[-5deg]">
                  <div className="rounded-[1.5rem] overflow-hidden">
                    <Image
                      src="/images/screenshot-tips.png"
                      alt="ValBewust App - Tips scherm"
                      width={168}
                      height={364}
                      sizes="168px"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center phone-screenshot">
              <div className="relative w-64 mx-auto mb-8">
                <div className="bg-gradient-to-br from-cta/10 to-cta/5 rounded-[2.5rem] p-4 shadow-xl">
                  <div className="rounded-[2rem] overflow-hidden">
                    <Image
                      src="/images/screenshot-home.png"
                      alt="ValBewust App - Home scherm"
                      width={224}
                      height={485}
                      sizes="224px"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
                <div className="absolute -bottom-3 -right-3 bg-yellow-400 text-cta-dark rounded-xl px-3 py-2 shadow-lg">
                  <div className="flex items-center gap-1">
                    <Star className="fill-cta-dark" size={14} />
                    <span className="font-bold text-sm">Gratis</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="https://apps.apple.com/nl/app/valbewust/id6759345024" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-ink text-white px-6 py-3 rounded-xl hover:bg-ink/90 transition-colors">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-xs text-white/70">Download in de</p>
                    <p className="font-semibold">App Store</p>
                  </div>
                </Link>
                <Link href="https://play.google.com/store/apps/details?id=nl.valbewust.thuis&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-ink text-white px-6 py-3 rounded-xl hover:bg-ink/90 transition-colors">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-xs text-white/70">Download in de</p>
                    <p className="font-semibold">Play Store</p>
                  </div>
                </Link>
              </div>
            </div>

            <div className="hidden lg:flex justify-start phone-screenshot">
              <div className="relative">
                <div className="w-48 bg-gradient-to-br from-cta/10 to-cta/5 rounded-[2rem] p-3 shadow-lg transform rotate-[5deg]">
                  <div className="rounded-[1.5rem] overflow-hidden">
                    <Image
                      src="/images/screenshot-chat.png"
                      alt="ValBewust App - Chat scherm"
                      width={168}
                      height={364}
                      sizes="168px"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="hoe-werkt-het" className="section-padding bg-gradient-to-br from-cta/5 to-cta/10 relative overflow-hidden">
        <div className="absolute top-20 -left-10 w-32 h-32 bg-cta/10 rounded-full" />
        <div className="absolute bottom-20 -right-10 w-24 h-24 bg-yellow-400/20 rounded-full" />

        <div className="container-site relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-3xl md:text-4xl font-normal text-ink mb-4">
                Hoe <span className="font-handwriting text-cta">werkt</span> de app?
              </h2>
              <p className="text-lg text-ink/70">
                In drie eenvoudige stappen naar een veiliger thuis
              </p>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white ring-4 ring-cta/20">
              <Image
                src="/images/elderly-phone-help.jpg"
                alt="Oudere leert smartphone gebruiken"
                width={600}
                height={288}
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="w-full h-64 md:h-72 object-cover"
              />
            </div>
          </div>

          <div ref={stepsRef} className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Download & Start', description: 'Download de gratis app en maak in een paar minuten uw persoonlijk profiel aan.', color: 'from-cta to-cta-light' },
              { step: '2', title: 'Ontdek & Leer', description: 'Bekijk tips, doe oefeningen en ontdek welke hulpmiddelen bij u passen.', color: 'from-cta-light to-cta' },
              { step: '3', title: 'Blijf Veilig', description: 'Ontvang notificaties met persoonlijke tips en houd uw voortgang bij.', color: 'from-cta to-cta-dark' },
            ].map((item) => (
              <div key={item.step} className="step-card bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden">
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${item.color}`} />
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center text-white font-bold text-xl`}>
                    {item.step}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-ink mb-3">{item.title}</h3>
                <p className="text-ink/70">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="container-site relative">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-normal text-ink mb-4">
              Wat vindt u in de <span className="font-handwriting text-cta">app</span>?
            </h2>
            <p className="text-lg text-ink/70">
              Alles wat u nodig heeft voor een veiliger leven
            </p>
          </div>

          <div ref={featuresRef} className="relative">
            <div className="hidden xl:flex flex-col gap-4 absolute -right-4 top-1/2 -translate-y-1/2 z-10">
              <div className="floating-image w-36 h-36 rounded-full overflow-hidden border-4 border-white shadow-xl transform -rotate-6 -translate-x-8">
                <Image src="/images/elderly-reading.jpg" alt="Oudere leest een boek" width={144} height={144} sizes="144px" className="w-full h-full object-cover" />
              </div>
              <div className="floating-image relative">
                <div className="w-48 h-48 rounded-full overflow-hidden border-6 border-white shadow-2xl transform rotate-3">
                  <Image src="/images/elderly-happy.jpg" alt="Blije oudere lacht" width={192} height={192} sizes="192px" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="floating-image w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-xl transform rotate-6 -translate-x-4">
                <Image src="/images/elderly-kitchen.jpg" alt="Oudere in de keuken" width={128} height={128} sizes="128px" className="w-full h-full object-cover" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 xl:pr-56">
              {[
                { icon: BookOpen, title: 'Dagelijkse Tips', description: 'Ontvang elke dag een praktische tip om vallen te voorkomen.', color: 'bg-cta/10 text-cta' },
                { icon: Dumbbell, title: 'Oefeningen', description: 'Eenvoudige balans- en krachtoefeningen die u thuis kunt doen.', color: 'bg-cta/10 text-cta' },
                { icon: ShoppingBag, title: 'Hulpmiddelen', description: 'Bekijk producten die helpen bij valpreventie in uw woning.', color: 'bg-cta/10 text-cta' },
                { icon: CheckCircle2, title: 'Huischeck', description: 'Loop door uw woning en ontdek verbeterpunten voor veiligheid.', color: 'bg-cta/10 text-cta' },
                { icon: BookOpen, title: 'Recepten', description: 'Gezonde recepten die bijdragen aan uw gezondheid en balans.', color: 'bg-cta/10 text-cta' },
                { icon: Bot, title: 'AI Assistent', description: 'Stel vragen aan onze slimme assistent over valpreventie.', color: 'bg-yellow-400/20 text-yellow-600', highlight: true },
              ].map((feature) => (
                <div
                  key={feature.title}
                  className={`feature-card rounded-2xl p-6 border-2 transition-all hover:shadow-lg ${
                    feature.highlight ? 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-yellow-100/50' : 'border-transparent bg-gray-50 hover:border-cta/20'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                    <feature.icon size={24} />
                  </div>
                  <h3 className="text-lg font-semibold text-ink mb-2">{feature.title}</h3>
                  <p className="text-ink/70 text-sm">{feature.description}</p>
                  {feature.highlight && (
                    <span className="inline-block mt-3 text-xs font-semibold text-yellow-600 bg-yellow-200 px-2 py-1 rounded-full">Nieuw!</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="section-padding bg-gradient-to-br from-cta to-cta-dark text-white relative overflow-hidden">
        <div className="absolute top-10 -left-16 w-40 h-40 bg-white/5 rounded-full" />
        <div className="absolute -bottom-10 right-1/4 w-32 h-32 bg-yellow-400/10 rounded-full" />

        <div className="container-site relative">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-normal mb-4">
              Wat <span className="font-handwriting text-yellow-300">gebruikers</span> zeggen
            </h2>
            {reviews.length > 0 && (
              <div className="flex items-center justify-center gap-2 text-yellow-300">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={24} className="fill-yellow-300" />
                ))}
                <span className="ml-2 text-white font-semibold">
                  {(reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)} uit 5
                </span>
              </div>
            )}
          </div>

          <div ref={reviewsRef}>
            {reviews.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Star className="w-10 h-10 text-yellow-300" />
                </div>
                <h3 className="text-2xl font-bold mb-3">Nog geen reviews</h3>
                <p className="text-white/80 text-lg mb-6">Wees de eerste die een review achterlaat!</p>
                <Link href="#download" className="btn-primary bg-white text-cta hover:bg-yellow-300 hover:text-cta-dark">
                  Download de App
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {reviews.map((review) => (
                  <div key={review.id} className="review-card bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} className={i < review.rating ? "text-yellow-300 fill-yellow-300" : "text-white/30"} />
                      ))}
                    </div>
                    <p className="text-white/90 mb-6 italic leading-relaxed">&ldquo;{review.comment}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                        <span className="text-lg font-bold">{review.firstName[0]}</span>
                      </div>
                      <div>
                        <p className="font-semibold">{review.firstName}</p>
                        <p className="text-white/60 text-sm">
                          {new Date(review.createdAt).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* About */}
      <section ref={aboutRef} className="section-padding bg-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-36 h-36 bg-cta/5 rounded-full" />
        <div className="absolute bottom-20 -left-16 w-32 h-32 bg-yellow-400/10 rounded-full" />

        <div className="container-site relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="about-content">
              <h2 className="text-3xl md:text-4xl font-normal text-ink mb-6">
                Over <span className="font-handwriting text-cta">ValBewust</span>
              </h2>
              <p className="text-lg text-ink/70 mb-6 leading-relaxed">
                ValBewust is ontstaan uit de overtuiging dat iedereen veilig en zelfstandig thuis moet kunnen wonen. Elk jaar vallen er in Nederland meer dan 100.000 ouderen, met vaak ernstige gevolgen.
              </p>
              <p className="text-lg text-ink/70 mb-8 leading-relaxed">
                Wij geloven dat veel van deze vallen voorkomen kunnen worden met de juiste kennis, oefeningen en hulpmiddelen. Daarom hebben we de ValBewust app ontwikkeld: een persoonlijke coach in uw broekzak.
              </p>

              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: Shield, title: 'Veiligheid voorop', sub: 'Preventie is beter dan genezen' },
                  { icon: Heart, title: 'Met zorg gemaakt', sub: 'Door en voor ouderen' },
                  { icon: Users, title: 'Gratis te gebruiken', sub: 'Geen verborgen kosten' },
                  { icon: MessageCircle, title: 'Persoonlijke hulp', sub: 'AI-assistent beschikbaar' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-cta/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="text-cta" size={20} />
                    </div>
                    <div>
                      <p className="font-semibold text-ink">{item.title}</p>
                      <p className="text-sm text-ink/60">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="about-image space-y-6">
              <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white ring-4 ring-cta/20">
                <Image
                  src="/images/elderly-couple-laptop.jpg"
                  alt="Ouder echtpaar geniet samen"
                  width={600}
                  height={288}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className="w-full h-64 md:h-72 object-cover"
                />
              </div>

              <div className="bg-gradient-to-br from-cta/5 to-cta/10 rounded-3xl p-6 md:p-8">
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="stat-number text-2xl md:text-3xl font-bold text-cta">100K+</p>
                    <p className="text-ink/60 text-xs mt-1">Vallen/jaar</p>
                  </div>
                  <div>
                    <p className="stat-number text-2xl md:text-3xl font-bold text-cta">30%</p>
                    <p className="text-ink/60 text-xs mt-1">Voorkoombaar</p>
                  </div>
                  <div>
                    <p className="stat-number text-2xl md:text-3xl font-bold text-cta">50+</p>
                    <p className="text-ink/60 text-xs mt-1">Tips & oefeningen</p>
                  </div>
                  <div>
                    <p className="stat-number text-2xl md:text-3xl font-bold text-cta">4.8</p>
                    <p className="text-ink/60 text-xs mt-1">Beoordeling</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-4 md:py-6 lg:py-8 pb-8 md:pb-10 lg:pb-12">
        <div className="container-site">
          <div ref={ctaRef} className="max-w-4xl mx-auto bg-gradient-to-r from-cta to-cta-dark rounded-[30px] p-8 md:p-10 pr-28 md:pr-36 flex flex-col md:flex-row items-center gap-8 relative">
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-normal text-white mb-3">
                Klaar om te <span className="font-handwriting text-yellow-400">starten?</span>
              </h2>
              <p className="text-white/80 mb-6">
                Download de gratis ValBewust app en maak uw huis veiliger voor u en uw dierbaren.
              </p>
              <Link href="#download" className="inline-flex items-center justify-center px-6 py-3 bg-white text-cta rounded-full font-bold transition-all duration-300 hover:scale-105 hover:bg-ink hover:text-white">
                <Smartphone className="mr-2" size={18} />
                Download Gratis
              </Link>
            </div>

            <div className="absolute right-0 translate-x-1/3 w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden flex-shrink-0 shadow-xl border-4 border-white bg-white flex items-center justify-center">
              <Image src="/images/icon.svg" alt="ValBewust" width={100} height={100} className="w-24 h-24 md:w-28 md:h-28" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
